import { HudPath, hudPaths } from './content';

export const hudResourcePaths = [
  hudPaths.termsOfService,
  hudPaths.privacyPolicy,
  hudPaths.cookiePolicy,
  hudPaths.promotionTerms
] as const;

export type HudResourcePath = (typeof hudResourcePaths)[number];

type ResourceSection = {
  title: string;
  body: readonly string[];
  bullets?: readonly string[];
};

export type HudResourceDocument = {
  path: HudResourcePath;
  id: string;
  title: string;
  subtitle: string;
  sourceUrl: string;
  effectiveDate?: string;
  telemetry: readonly { label: string; value: number }[];
  sections: readonly ResourceSection[];
};

export const hudResourceDocuments: readonly HudResourceDocument[] = [
  {
    path: hudPaths.termsOfService,
    id: 'RES-01',
    title: 'Terms of Service',
    subtitle:
      'Operational terms governing access to DeveLogic Digital services and platform interactions.',
    sourceUrl: 'https://develogic-digital.com/index.php/terms-of-service/',
    telemetry: [
      { label: 'Contract Clarity', value: 96 },
      { label: 'Service Scope', value: 94 },
      { label: 'Risk Coverage', value: 92 }
    ],
    sections: [
      {
        title: 'Overview',
        body: [
          'These Terms of Service govern access to and use of services provided by DeveLogic Digital through its website and related platforms.',
          'By accessing or using the services, users agree to be bound by these terms and conditions.'
        ]
      },
      {
        title: '1. Description of Services',
        body: [
          'DeveLogic offers software development, web design and development, prototyping, management solutions, optimization services, automation services, and related technical services.'
        ]
      },
      {
        title: '2. User Responsibilities',
        body: ['By accessing services, users agree to comply with operational and legal responsibilities.'],
        bullets: [
          'Provide accurate and complete information where required.',
          'Use services for lawful purposes only.',
          'Respect the intellectual property rights of DeveLogic and third parties.',
          'Do not attempt unauthorized access to any service component.'
        ]
      },
      {
        title: '3. Intellectual Property',
        body: [
          'All content, designs, logos, trademarks, and materials provided by DeveLogic are protected by intellectual property laws.',
          'Reproduction, distribution, modification, or derivative use requires prior written consent.'
        ]
      },
      {
        title: '4. Payment Terms',
        body: [
          'Payment terms are agreed between DeveLogic and each client and are defined in applicable agreements or invoices.'
        ]
      },
      {
        title: '5. Limitation of Liability',
        body: [
          'DeveLogic is not liable for indirect, incidental, special, consequential, or punitive damages arising from use or inability to use services.'
        ]
      },
      {
        title: '6. Termination',
        body: [
          'Access may be suspended or terminated at DeveLogic discretion without prior notice for term violations or other valid operational reasons.'
        ]
      },
      {
        title: '7. Privacy',
        body: [
          'Personal information handling is governed by the Privacy Policy, including data collection, use, and disclosure practices.'
        ]
      },
      {
        title: '8. Modifications',
        body: [
          'Terms may be modified or replaced at any time. Continued use after updates constitutes acceptance of those changes.'
        ]
      },
      {
        title: '9. Governing Law',
        body: ['These terms are governed by the laws of South Africa, without regard to conflict of law provisions.']
      },
      {
        title: '10. Contact',
        body: ['Questions regarding these terms can be directed to info@develogic-digital.com.']
      }
    ]
  },
  {
    path: hudPaths.privacyPolicy,
    id: 'RES-02',
    title: 'Privacy Policy',
    subtitle:
      'Data handling protocol for collection, processing, disclosure, and security of personal and usage information.',
    sourceUrl: 'https://develogic-digital.com/index.php/privacy-policy/',
    effectiveDate: '08-04-2024',
    telemetry: [
      { label: 'Data Controls', value: 95 },
      { label: 'Security Posture', value: 91 },
      { label: 'Disclosure Policy', value: 93 }
    ],
    sections: [
      {
        title: 'Overview',
        body: [
          'This Privacy Policy describes how DeveLogic Digital collects, uses, and discloses information when users access website and service channels.',
          'Use of services indicates agreement with this policy.'
        ]
      },
      {
        title: '1. Information Collection and Use',
        body: [
          'Personal information may include name, email address, company name, and submitted request details when users contact DeveLogic.',
          'Usage data may include IP address, browser type, pages visited, and similar interaction statistics.'
        ]
      },
      {
        title: '2. Use of Information',
        body: ['Collected information may be used to support and improve service operations.'],
        bullets: [
          'Respond to inquiries and provide customer support.',
          'Customize and improve service quality.',
          'Analyze trends and behavior for platform optimization.',
          'Send relevant service, promotion, or update communications.'
        ]
      },
      {
        title: '3. Disclosure of Information',
        body: [
          'Information may be shared with third-party service providers supporting operations, subject to confidentiality requirements.',
          'Information may also be disclosed where legally required or necessary to protect rights, property, or safety.'
        ]
      },
      {
        title: '4. Data Security',
        body: [
          'Reasonable measures are used to protect against unauthorized access, disclosure, or alteration.',
          'No internet transmission or electronic storage method can be guaranteed 100% secure.'
        ]
      },
      {
        title: '5. Links to Other Sites',
        body: [
          'Third-party links may appear on the site. DeveLogic is not responsible for third-party content, privacy policies, or practices.'
        ]
      },
      {
        title: "6. Children's Privacy",
        body: [
          'Services are not directed to individuals under 18 years of age.',
          'If a parent or guardian believes personal information from a child has been submitted, DeveLogic can be contacted to take appropriate action.'
        ]
      },
      {
        title: '7. Policy Updates',
        body: [
          'Privacy policy updates may be posted on this page. Users should review periodically for changes.'
        ]
      },
      {
        title: '8. Contact',
        body: ['Questions regarding this policy can be directed to info@develogic-digital.com.']
      }
    ]
  },
  {
    path: hudPaths.cookiePolicy,
    id: 'RES-03',
    title: 'Cookie Policy',
    subtitle:
      'Tracking technology policy for cookie usage, cookie categories, third-party integrations, and user controls.',
    sourceUrl: 'https://develogic-digital.com/index.php/cookie-policy/',
    effectiveDate: '08-04-2024',
    telemetry: [
      { label: 'Tracking Governance', value: 94 },
      { label: 'Cookie Controls', value: 90 },
      { label: 'Consent Clarity', value: 92 }
    ],
    sections: [
      {
        title: 'Overview',
        body: [
          'This policy explains how DeveLogic Digital uses cookies and similar tracking technologies when users visit the website.'
        ]
      },
      {
        title: '1. What Are Cookies?',
        body: [
          'Cookies are small text files stored on devices when visiting websites and are used to improve site function and provide usage information.'
        ]
      },
      {
        title: '2. How Cookies Are Used',
        body: ['Cookie usage supports functional, analytical, and campaign objectives.'],
        bullets: [
          'Essential cookies for core security and accessibility functions.',
          'Analytics cookies for traffic analysis and user-experience optimization.',
          'Marketing cookies for personalized content and campaign effectiveness tracking.'
        ]
      },
      {
        title: '3. Cookie Types',
        body: [
          'Session cookies are temporary and removed when browser sessions end.',
          'Persistent cookies remain for longer durations to preserve user preferences and settings.'
        ]
      },
      {
        title: '4. Third-Party Cookies',
        body: [
          'Trusted third-party providers may supply cookies for analytics, advertising, and operational purposes.'
        ]
      },
      {
        title: '5. Cookie Choices',
        body: [
          'Users can accept or reject cookies through browser settings.',
          'Disabling cookies may affect site experience and reduce functionality of certain features.'
        ]
      },
      {
        title: '6. Policy Updates',
        body: [
          'Cookie policy updates may occur to reflect operational, legal, or regulatory changes and are published with updated effective dates.'
        ]
      },
      {
        title: '7. Contact',
        body: [
          'Questions regarding cookie practices can be directed to info@develogic-digital.com.',
          'Website use indicates consent to cookie usage as described in this policy.'
        ]
      }
    ]
  },
  {
    path: hudPaths.promotionTerms,
    id: 'RES-04',
    title: "Promotion Terms and Conditions",
    subtitle: 'Promotional offer framework, eligibility constraints, discount handling, and participation limits.',
    sourceUrl: 'https://develogic-digital.com/index.php/promotion-ts-and-cs/',
    telemetry: [
      { label: 'Offer Integrity', value: 93 },
      { label: 'Eligibility Controls', value: 92 },
      { label: 'Terms Enforcement', value: 95 }
    ],
    sections: [
      {
        title: 'Overview',
        body: [
          'These promotion terms govern offers provided by DeveLogic Digital and apply to participation in active promotional campaigns.'
        ]
      },
      {
        title: '1. Promotion Details',
        body: [
          'Promotions apply to clients signing up for development services during the advertised promotional period.',
          'Qualifying clients may receive up to 50% off selected development services, depending on the active offer terms.'
        ]
      },
      {
        title: '2. Eligibility',
        body: [
          'Promotions are generally open to new clients, and may include existing clients where specified.',
          'Eligibility requires compliance with these terms and any additional offer-specific conditions.'
        ]
      },
      {
        title: '3. Participation',
        body: [
          'Eligible clients must sign up during the active period and satisfy any required criteria or information requests defined in the promotional offer.'
        ]
      },
      {
        title: '4. Discount Calculation and Application',
        body: [
          'Discounts apply to qualifying development service costs specified in the signed agreement.',
          'Discount values may be percentage-based or fixed amounts according to offer terms and verification outcomes.'
        ]
      },
      {
        title: '5. Exclusions and Limitations',
        body: [
          'Promotions cannot be combined with other offers unless explicitly stated.',
          'Offer availability, discontinuation, and project exclusions remain at DeveLogic discretion.'
        ]
      },
      {
        title: '6. General Terms',
        body: [
          'DeveLogic may verify participant eligibility and disqualify abusive or fraudulent participation.',
          'Terms may change without notice and become effective when published.'
        ]
      },
      {
        title: '7. Contact',
        body: ['Questions regarding promotions can be directed to info@develogic-digital.com.']
      }
    ]
  }
] as const;

export const hudResourceDocumentByPath = hudResourceDocuments.reduce(
  (accumulator, document) => {
    accumulator[document.path] = document;
    return accumulator;
  },
  {} as Record<HudResourcePath, HudResourceDocument>
);

export const isHudResourcePath = (path: HudPath): path is HudResourcePath =>
  (hudResourcePaths as readonly HudPath[]).includes(path);
